import React, { useState, FC, createContext, useEffect, ReactElement } from "react";
import { Alert, Button, Col, Container, Form, Modal, Navbar, Row, Spinner } from "react-bootstrap";
import { graphConfig, loginRequest } from "../authConfig";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { AuthenticationResult, EventMessage, EventType } from "@azure/msal-browser";
import { ISelectOption } from "utilities/FormUtility";
import { getParamFromQuerystring } from "utilities/BrowserUtility";
import useLocalStorage from 'react-use-localstorage';
import MiscService from "services/MiscService";

import logo from '../assets/images/tft-logo-rgb-pos.png';
import { useNavigate } from "react-router-dom";
import ISalesRep from "models/sql/ISalesRep";

export interface IGlobalContextProps {
    loading: boolean;
    isAuthenticated: boolean;
    userName: string;
    userEmail: string;
    repEntry?: ISalesRep;
    roles: string[];
    userType?: number;
    getAccessTokenAsync: () => Promise<string>;
    setLoading: (state: boolean) => void;
    setTitle: (title: string) => void;
    setSecondaryNav: (content: React.ReactElement) => void;
    showModal: (settings: IModalSettings) => void;
    hideModal: () => void;
    showErrorMessage: (message: string, timeout?: number, variant?: string) => void;
    region?: IGlobalRegion;
    forceUpdateGlobalFilters?: Date;
    organizationType?: string;
}

export const GlobalContext = createContext<IGlobalContextProps>({
    loading: false,
    isAuthenticated: false,
    userName: "",
    userEmail: "",
    repEntry: undefined,
    roles: [],
    userType: 0,
    getAccessTokenAsync: () => new Promise((resolve, reject) => resolve("")),
    setLoading: (state: boolean) => { },
    setTitle: (title: string) => { },
    setSecondaryNav: (content: React.ReactElement) => { },
    showModal: (settings: IModalSettings) => { },
    hideModal: () => { },
    showErrorMessage: (message: string, timeout?: number, variant?: string) => { },
    region: undefined,
    forceUpdateGlobalFilters: undefined,
    organizationType: undefined
});

interface GlobalContextWrapperProps {
    children: React.ReactNode;
}

export interface IModalFormElement {
    type: "text" | "select" | "date" | "time" | "datetime" | "textarea" | "multiselect" | "adder";
    key: string;
    title: string;
    value?: string;
    options?: ISelectOption[];
    style?: React.CSSProperties;
    className?: string;
    useFilter?: boolean;
    filterPhrase?: string;
    adderBuffer?: string;
    adderBuffer2?: string;
    required?: boolean;
    error?: string;
};

interface ICustomActionButton {
    title: string;
    onClick: () => void;
    buttonType?: string;
    closesModal?: boolean;
}

interface IModalSettings {
    content: ReactElement | string;
    title?: ReactElement | string;
    okCallback?: (data: IModalFormElement[] | null) => void;
    cancelCallback?: () => void;
    okButtonTitle?: string;
    cancelButtonTitle?: string;
    formElements?: IModalFormElement[];
    size?: "sm" | "lg" | "xl";
    customActionButton?: ICustomActionButton;
    fullScreen?: true | "sm-down" | "md-down" | "lg-down" | "xl-down" | "xxl-down";
};

export interface IGlobalRegion {
    title: string,
    states: string[],
    countries: string[],
    repEmail: string,
    repName: string
};

const regions: IGlobalRegion[] = [
    {
        title: "All Domestic",
        states: [],
        countries: ["United States"],
        repEmail: "",
        repName: ""
    },
    {
        title: "All International",
        states: [],
        countries: ["Australia", "Hong Kong", "Indonesia", "Japan", "Malaysia", "Mongolia", "New Zealand", "China", "Singapore", "South Africa", "South Korea", "Thailand", "Vietnam", "Belgium", "France", "Germany", "Ireland", "Netherlands", "Portugal", "Spain", "United Kingdom", "Austria", "Belarus", "Bosnia", "Croatia", "Czech Republic", "Denmark", "Estonia", "Finland", "Greece", "Hungary", "Italy", "Lithuania", "Macedonia", "Norway", "Philippines", "Poland", "Romania", "Slovenia", "Sweden", "Switzerland", "Bahrain", "Egypt", "India", "Iraq", "Israel", "Jordan", "Kazakhstan", "Kuwaint", "Oman", "Pakistan", "Qatar", "Saudi Arabia", "Serbia", "Turkey", "United Arab Emirates", "Argentina", "Bolivia", "Brazil", "Chile", "Colombia", "Ecuador", "El Salvador", "Guatemala", "Mexico", "Peru", "Uraguay", "Venezuela"],
        repEmail: "",
        repName: ""
    },
    {
        title: "US Pacific",
        states: ["AK", "CA", "HI", "ID", "OR", "WA"],
        countries: ["United States"],
        repEmail: "rob.meek@tft.com",
        repName: "Rob Meek"
    },
    {
        title: "US Intermountain",
        states: ["AZ", "CO", "NV", "NM", "UT", "WY", "MT"],
        countries: ["United States"],
        repEmail: "aj.bonnett@tft.com",
        repName: "AJ Bonnett"
    },
    {
        title: "US North Central",
        states: ["ND", "SD", "MN", "WI", "IA", "NE", "KS", "MO", "OK", "AR"],
        countries: ["United States"],
        repEmail: "matt.carlson@tft.com",
        repName: "Matt Carlson"
    },
    {
        title: "US Midwest",
        states: ["MI", "IL", "IN", "OH", "KY", "TN", "WV"],
        countries: ["United States"],
        repEmail: "kevin.briggs@tft.com",
        repName: "Kevin Briggs"
    },
    {
        title: "US Northeast",
        states: ["ME", "VT", "NH", "MA", "CT", "RI", "NY", "DE", "MD", "PA", "NJ"],
        countries: ["United States"],
        repEmail: "tom.houlihan@tft.com",
        repName: "Tom Houlihan"
    },
    {
        title: "US Southeast",
        states: ["VA", "NC", "SC", "GA", "FL"],
        countries: ["United States"],
        repEmail: "ron.prast@tft.com",
        repName: "Ron Prast"
    },
    {
        title: "US South",
        states: ["TX", "LA", "MS", "AL"],
        countries: ["United States"],
        repEmail: "benjamin.lehman@tft.com",
        repName: "Ben Lehman"
    },
    {
        title: "INT Canada",
        states: [],
        countries: ["Canada"],
        repEmail: "jeff.goarley@tft.com",
        repName: "Jeff Goarley"
    },
    {
        title: "INT Asia Pacific",
        states: [],
        countries: ["Australia", "Hong Kong", "Indonesia", "Japan", "Malaysia", "Mongolia", "New Zealand", "China", "Singapore", "South Africa", "South Korea", "Thailand", "Vietnam"],
        repEmail: "shaun.price@tft.com",
        repName: "Shaun Price"
    },
    {
        title: "INT EU1",
        states: [],
        countries: ["Belgium", "France", "Germany", "Ireland", "Netherlands", "Portugal", "Spain", "United Kingdom"],
        repEmail: "christian.boorz@tft.com",
        repName: "Christian Boorz"
    },
    {
        title: "INT EU2",
        states: [],
        countries: ["Austria", "Belarus", "Bosnia", "Croatia", "Czech Republic", "Denmark", "Estonia", "Finland", "Greece", "Hungary", "Italy", "Lithuania", "Macedonia", "Norway", "Philippines", "Poland", "Romania", "Slovenia", "Sweden", "Switzerland"],
        repEmail: "thomas.voelkel@tft.com",
        repName: "Thomas Voelkel"
    },
    {
        title: "INT Middle East",
        states: [],
        countries: ["Bahrain", "Egypt", "India", "Iraq", "Israel", "Jordan", "Kazakhstan", "Kuwaint", "Oman", "Pakistan", "Qatar", "Saudi Arabia", "Serbia", "Turkey", "United Arab Emirates"],
        repEmail: "hadi.jisri@tft.com",
        repName: "Hadi Jisri"
    },
    {
        title: "INT Latin America",
        states: [],
        countries: ["Argentina", "Bolivia", "Brazil", "Chile", "Colombia", "Ecuador", "El Salvador", "Guatemala", "Mexico", "Peru", "Uraguay", "Venezuela"],
        repEmail: "sebastian.delvalle@tft.com",
        repName: "Sebastian Delvalle"
    }];

export const OrganizationTypes = [
    "Municipal",
    "State Government FD",
    "All Industrial",
    "Industrial FD",
    "Industrial, Other",
    "Federal, DOD",
    "Federal, Other",
    "Transportation FD",
    "Contract FD",
    "Tribal Government",
    "Other"
];

const GlobalContextWrapper: FC<GlobalContextWrapperProps> = (props: GlobalContextWrapperProps) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorCustomVariant, setErrorCustomVariant] = useState("");
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const [graphData, setGraphData] = useState<any>(null);
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [repEntry, setRepEntry] = useState<ISalesRep>();
    const [title, setTitle] = useState("");
    const [secondaryNav, setSecondaryNav] = useState<React.ReactElement>();
    const [modalSettings, setModalSettings] = useState<IModalSettings | null>(null);
    const [modalFormData, setModalFormData] = useState<IModalFormElement[] | null>(null);
    const [roles, setRoles] = useState<string[]>([]);
    const [storedRegionTitle, setStoredRegionTitle] = useLocalStorage('storedRegionTitle', "");
    const [region, setRegion] = useState<IGlobalRegion | undefined>(regions.find(r => r.title === storedRegionTitle));
    const [forceUpdateGlobalFilters, setForceUpdateGlobalFilters] = useState<Date>();
    const [storedOrganizationType, setStoredOrganizationType] = useLocalStorage('storedOrganizationType', "");
    const [organizationType, setOrganizationType] = useState<string | undefined>(storedOrganizationType);
    const [userType, setUserType] = useState<number | undefined>(undefined);
    const navigate = useNavigate();

    const showModal = (settings: IModalSettings) => {
        if (settings.formElements) {
            // default any select boxes if the current valueis empty
            settings.formElements.forEach(
                entry => {
                    if (entry.type === "select" && entry.options?.[0].value !== null && entry.options?.[0].value !== undefined) {
                        entry.value = entry.options?.[0].value;
                    }
                });

            setModalFormData(settings.formElements);
            window.setTimeout(
                () => {
                    (document.querySelectorAll("#globalModal input, #globalModal textarea, #globalModal select")[0] as HTMLElement).focus();
                },
                20);
        }
        setModalSettings(settings);
    };

    const hideModal = () => {
        setModalSettings(null);
    };

    const getProfileAndTokenAsync = async (msalInstance: any) => {
        const account = msalInstance.getActiveAccount();

        if (!account) {
            throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
        }

        const request = {
            ...loginRequest,
            account: account
        };

        const response =
            await msalInstance.acquireTokenSilent(request)
                .catch(async (error: any) => {
                    return msalInstance.acquireTokenRedirect(request);
                });

        const headers = new Headers();
        const bearer = `Bearer ${response.accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers,
        };

        return fetch(graphConfig.graphMeEndpoint, options)
            .then(response => response.json())
            .catch(error => console.log(error));
    }

    const showErrorMessage = (message: string, timeout?: number, variant?: string) => {
        if (variant) {
            setErrorCustomVariant(variant);
        }

        setErrorMessage(message);

        if (timeout) {
            setTimeout(
                () => {
                    setErrorMessage("");
                }, timeout);
        }
    }

    const RequestProfileDataAsync = async () => {
        try {
            let graphResult = await getProfileAndTokenAsync(instance);
            setGraphData(graphResult);
            setUserName(graphResult.displayName);
            setUserEmail(graphResult.mail);
            setUserType(0);
            await getAccessTokenAsync();
        }
        catch (e: any) {
            console.log("Couldn't get graph data");
        }
    };

    const getAccessTokenAsync = async () => {
        if (userType && userType > 0) {
            return "";
        }

        const account = instance.getActiveAccount();

        if (!account) {
            throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
        }

        const accessTokenRequest = {
            scopes: ["api://c33d2bc1-1b21-4983-95d5-80dd1345e9e6/access_as_user"],
            account: account
        }

        const response =
            await instance.acquireTokenSilent(accessTokenRequest)
                .catch(async (error: any) => {
                    instance.acquireTokenRedirect(accessTokenRequest);
                });

        // @ts-ignore
        setRoles(response.account?.idTokenClaims?.roles || []);
        return response?.accessToken ?? "";
    }

    useEffect(
        () => {
            instance.addEventCallback((event: EventMessage) => {
                if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                    const payload = event.payload as AuthenticationResult;
                    const account = payload.account;
                    instance.setActiveAccount(account);
                }
            });

            (async () => {
                if (isAuthenticated) {
                    setLoading(true);
                    await RequestProfileDataAsync();
                    const accessToken = await getAccessTokenAsync();
                    let userInfoResult = await MiscService.getUserInfoAsync(accessToken);

                    if (userInfoResult) {
                        setRepEntry(userInfoResult.repEntry);
                    }

                    setLoading(false);
                }
                else {
                    if (!userType) {
                        setLoading(true);
                        const DashboardToken = getParamFromQuerystring("dashboardToken") ?? "pass";
                        try {
                            const result = await MiscService.verifyDashboardTokenAsync(DashboardToken);
                            // @ts-ignore
                            setUserType(parseInt(result["type"]));
                            // @ts-ignore
                            setRoles(result["roles"].split(","));
                            // @ts-ignore
                            setUserName(result["name"]);
                            // @ts-ignore
                            setUserEmail(result["email"]);
                            navigate("/");
                        }
                        catch (e) {

                        }
                        setLoading(false);
                    }
                }
            })();
        }, [isAuthenticated, instance]);

    const handleLogout = (instance: any) => {
        instance
            .logoutRedirect()
            .catch((e: any) => {
                console.error(e);
            });
    };

    const handleLogin = (instance: any) => {
        instance
            .loginRedirect(loginRequest)
            .catch((e: any) => {
                console.error(e);
            });
    };

    const [repAccessEmail, setRepAccessEmail] = useState("");
    const [showRepAccessLookup, setShowRepAccessLookup] = useState(false);

    const requestRepAccess = () => {
        setRepAccessEmail("");
        setShowRepAccessLookup(true);
    };

    const submitRepAccessEmailAsync = async () => {
        setLoading(true);

        setTimeout(() => {
            document.querySelector("#requestFooter")?.setAttribute("style", "display: none");
        }, 10);

        const result = await MiscService.requestDashboardAccessAsync(repAccessEmail);
        document.querySelector(".request-content")?.setAttribute("style", "display: none");
        document.querySelector(".request-thank-you")?.setAttribute("style", "display: block");
        //setTimeout(() => hideRepAccessLookupModal(), 2000);
        setLoading(false);
    };

    const hideRepAccessLookupModal = () => {
        setShowRepAccessLookup(false);
    };

    const loggedInContent = () => <>
        <Container>
            <Row className="no-print">
                <Navbar expand="lg" className="nav">
                    <Navbar.Brand
                        style={{ cursor: "pointer", padding: "0" }}
                        onClick={() => navigate("/")}>
                        <img
                            src={logo}
                            width="200"
                            className="d-inline-block align-top"
                            alt="TFT Department Profile System"
                        />
                    </Navbar.Brand>
                    <Navbar.Text className="ms-auto nav-title">
                        {title && <strong>{title}</strong>}
                    </Navbar.Text>
                    <Navbar.Text className="ms-auto nav-logout" style={{ textAlign: "right" }}>
                        <strong>{userName}</strong>&nbsp;{userType === 0 && <Button variant="secondary" className="ml-auto" onClick={() => handleLogout(instance)}>Sign out</Button>}<br />
                        {userType === 0 &&
                            <>
                                <Form.Select
                                    className="mt-2"
                                    onChange={async (e) => {
                                        const chosenRegion = regions.find(r => r.title === e.target.value);
                                        console.log("Region Changed", chosenRegion);
                                        setRegion(chosenRegion);
                                        setForceUpdateGlobalFilters(new Date());
                                        setStoredRegionTitle(chosenRegion?.title || "");
                                    }}
                                    value={region ? region.title : ""}>
                                    <option key={"empty"} value={""}>All Regions</option>
                                    {
                                        regions.map((s, index) => <option key={s.title} value={s.title}>{s.title}</option>)
                                    }
                                </Form.Select>
                                <Form.Select
                                    className="mt-2"
                                    onChange={async (e) => {
                                        console.log("Type Changed", organizationType);
                                        setOrganizationType(e.currentTarget.value);
                                        setForceUpdateGlobalFilters(new Date());
                                        setStoredOrganizationType(e.currentTarget.value);
                                    }}
                                    value={organizationType}>
                                    <option key={"empty"} value={""}>All Organization Types</option>
                                    {
                                        OrganizationTypes.map(s => <option key={s} value={s}>{s}</option>)
                                    }
                                </Form.Select>
                            </>
                        }
                    </Navbar.Text>
                </Navbar>
            </Row>
            {
                secondaryNav &&
                <Row>
                    <Navbar expand="sm" className="secondary-nav">
                        {
                            secondaryNav
                        }
                    </Navbar>
                </Row>
            }
        </Container>
        <Container role="content" >
            {props.children && props.children}
        </Container>
    </>;

    const loggedOutContentRep = () => <>
        <Container className="p-5 mb-4  text-center login-container">
            <h1 className="header login-header">
                <img
                    src={logo}
                    alt="TFT Department Profile System"
                    width="200" />
            </h1><br /><br />
            <h2 className="login-content">
                Welcome, click here to <Button variant="primary" className="ml-auto" onClick={requestRepAccess}>Access My Dashboard</Button>
            </h2><br />

            <strong>If you are looking for the Health Risk Assessment, please <a href="/public/hra?id=home" target="_blank">CLICK HERE</a>.</strong>

            <Modal show={showRepAccessLookup} id="lookupModal" onHide={hideRepAccessLookupModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Rep Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="request-content">
                        <p>Please submit your email and we'll send you a temporary link to access your Rep Portal.</p>
                        <Form>
                            <Form.Group className="mb-3" controlId="id">
                                <Form.Control
                                    type="text"
                                    placeholder="Rep@Company.com"
                                    value={repAccessEmail}
                                    onChange={(e) => setRepAccessEmail(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            submitRepAccessEmailAsync();
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }
                                    }} />
                            </Form.Group>
                        </Form>
                    </div>
                    <div className="request-thank-you" style={{ display: "none" }}>
                        <h1>Request Sent</h1>
                        <ul>
                            <li style={{ paddingTop: "10px" }}>Check your email for a login token. If you are approved to access this site you will receive an email immediately.</li>
                            <li>The link you receive will allow you to access this site for 3 days.</li>
                        </ul>
                    </div>
                </Modal.Body>
                <Modal.Footer id="requestFooter">
                    <Button variant="secondary" onClick={hideRepAccessLookupModal}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={submitRepAccessEmailAsync}>
                        Submit Request
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    </>;

    const loggedOutContentAdmin = () => <>
        <Container className="p-5 mb-4  text-center login-container">
            <h1 className="header login-header">
                <img
                    src={logo}
                    alt="TFT Department Profile System"
                    width="200" />
            </h1><br /><br />
            <h2 className="login-content">
                Welcome, please <Button variant="primary" className="ml-auto" onClick={() => handleLogin(instance)}>Sign in</Button>
            </h2><br />

        </Container>
    </>;

    const modalOk = () => {
        let validData = true;
        modalFormData?.forEach(
            entry => {
                if (entry.required && (!entry.hasOwnProperty("value") || entry.value === null || entry.value === "")) {
                    validData = false;
                    entry.error = "This is required";
                }
                else {
                    entry.error = "";
                }
            });

        setModalFormData([...(modalFormData || [])]);

        if (validData) {
            if (modalSettings?.okCallback) {
                modalSettings?.okCallback(modalFormData ? [...modalFormData] : null);
            }
            setModalSettings(null);
            setModalFormData(null);
        }
        else {
        }
    };

    return (
        <>
            {
                loading &&
                <div className="loading-display">
                    <div className="content">
                        <h2>
                            <Spinner animation="border" role="status"></Spinner>
                            &nbsp;Loading...
                        </h2>
                    </div>
                </div>
            }
            {
                errorMessage &&
                <Alert
                    variant={errorCustomVariant || "danger"}
                    onClose={() => setErrorMessage("")}
                    dismissible>
                    {errorMessage}
                </Alert>
            }
            <GlobalContext.Provider value={{ loading, setLoading, forceUpdateGlobalFilters, showErrorMessage, getAccessTokenAsync, isAuthenticated, userName, userEmail, repEntry, setTitle, setSecondaryNav, showModal, hideModal, roles, userType, region, organizationType }}>
                <div style={{ display: loading ? "none" : "" }}>
                    {
                        ((isAuthenticated || (userType && userType > 0)) && userName) ?
                            loggedInContent() :
                            (window.location.pathname.toLowerCase().indexOf("/login") === 0) ?
                                loggedOutContentAdmin() :
                                loggedOutContentRep()
                    }
                </div>
                <Modal
                    id="globalModal"
                    show={modalSettings != null}
                    size={modalSettings?.size || "lg"}
                    fullscreen={modalSettings?.fullScreen || undefined}
                    onHide={() => {
                        if (modalSettings?.cancelCallback) {
                            modalSettings.cancelCallback();
                        }

                        setModalSettings(null);
                        setModalFormData(null);
                    }}>
                    {
                        modalSettings?.title &&
                        <Modal.Header closeButton>
                            <Modal.Title>{modalSettings.title}</Modal.Title>
                        </Modal.Header>
                    }

                    <Modal.Body>
                        <Row className="align-items-end">
                            <Col>
                                {modalSettings?.content}
                            </Col>
                        </Row>
                        {
                            modalSettings?.formElements &&
                            <Form
                                onSubmit={(e) => {
                                    modalOk();
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}>
                                {
                                    modalSettings?.formElements?.map(
                                        (element, index) =>
                                            <Row className="align-items-end" key={index}>
                                                <Col>
                                                    <Form.Group className="mb-3" >
                                                        <Form.Label>{element.title}</Form.Label>
                                                        {
                                                            element.error &&
                                                            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>{element.error}</Form.Control.Feedback>
                                                        }
                                                        {
                                                            element.type === "text" &&
                                                            <Form.Control
                                                                type="text"
                                                                style={element.style}
                                                                className={element.className}
                                                                isInvalid={!!element.error}
                                                                onChange={
                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        element.value = e.target.value;
                                                                        setModalFormData([...(modalFormData || [])]);
                                                                    }
                                                                }
                                                                value={element.value}
                                                            />
                                                        }
                                                        {
                                                            element.type === "textarea" &&
                                                            <Form.Control
                                                                as="textarea"
                                                                style={element.style}
                                                                className={element.className}
                                                                isInvalid={!!element.error}
                                                                onChange={
                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        element.value = e.target.value;
                                                                        setModalFormData([...(modalFormData || [])]);
                                                                    }
                                                                }
                                                                value={element.value}
                                                            />
                                                        }
                                                        {
                                                            element.type === "select" &&
                                                            <Form.Select
                                                                value={element.value}
                                                                style={element.style}
                                                                className={element.className}
                                                                isInvalid={!!element.error}
                                                                onChange={
                                                                    (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                                        element.value = e.target.value;
                                                                        setModalFormData([...(modalFormData || [])]);
                                                                    }
                                                                }>
                                                                {(element.options || []).map((option, index) => <option key={option.value} value={option.value}>{option.title}</option>)}
                                                            </Form.Select>
                                                        }
                                                        {
                                                            element.type === "multiselect" &&
                                                            <>
                                                                {element.useFilter &&
                                                                    <>
                                                                        <Form.Control
                                                                            type="text"
                                                                            onChange={
                                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                    element.filterPhrase = e.currentTarget.value;
                                                                                    setModalFormData([...(modalFormData || [])]);
                                                                                }
                                                                            }
                                                                            value={element.filterPhrase}
                                                                        /><br />
                                                                    </>
                                                                }
                                                                <Form.Select
                                                                    multiple={true}
                                                                    style={element.style}
                                                                    className={element.className}
                                                                    isInvalid={!!element.error}
                                                                    value={(element.value || "")?.split(",")}
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                                            const options = e.target.selectedOptions;
                                                                            // @ts-ignore
                                                                            element.value = [].slice.call(options).map(o => o.value).join(",");
                                                                            setModalFormData([...(modalFormData || [])]);
                                                                        }
                                                                    }>
                                                                    {
                                                                        (element.options || [])
                                                                            .filter(
                                                                                o =>
                                                                                    element.filterPhrase && o ?
                                                                                        o.title.toLowerCase().indexOf(element.filterPhrase.toLowerCase()) > -1 :
                                                                                        true)
                                                                            .map(option => <option key={option.value} value={option.value}>{option.title}</option>)
                                                                    }
                                                                </Form.Select>
                                                            </>
                                                        }
                                                        {
                                                            element.type === "adder" &&
                                                            <>
                                                                {element.useFilter &&
                                                                    <>
                                                                        <br /><strong>Filter available options</strong>
                                                                        <Form.Control
                                                                            type="text"
                                                                            className="mb-1"
                                                                            onChange={
                                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                    element.filterPhrase = e.currentTarget.value;
                                                                                    setModalFormData([...(modalFormData || [])]);
                                                                                }
                                                                            }
                                                                            value={element.filterPhrase}
                                                                        />
                                                                    </>
                                                                }
                                                                <Form.Select
                                                                    multiple={true}
                                                                    style={element.style}
                                                                    className={element.className}
                                                                    isInvalid={!!element.error}
                                                                    value={(element.adderBuffer || "")?.split(",")}
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                                            const options = e.target.selectedOptions;
                                                                            // @ts-ignore
                                                                            element.adderBuffer = [].slice.call(options).map(o => o.value).join(",");
                                                                            setModalFormData([...(modalFormData || [])]);
                                                                        }
                                                                    }>
                                                                    {
                                                                        (element.options || [])
                                                                            .filter(
                                                                                (o: any) =>
                                                                                    ((element.value || "")
                                                                                        .split(",") || [])
                                                                                        .findIndex(a => a === o.value) === -1)
                                                                            .filter((o: any) => element.filterPhrase && o ?
                                                                                o.title.toLowerCase().indexOf(element.filterPhrase.toLowerCase()) > -1 :
                                                                                true)
                                                                            .map(option => <option key={option.value} value={option.value}>{option.title}</option>)
                                                                    }
                                                                </Form.Select>
                                                                <div className="text-center">
                                                                    <Button
                                                                        className="m-3"
                                                                        style={{ width: "45%" }}
                                                                        onClick={(e) => {
                                                                            element.value = (element?.value ? element.value + "," : "") + element.adderBuffer;
                                                                            setModalFormData([...(modalFormData || [])]);
                                                                        }}>
                                                                        Add Selected
                                                                    </Button>
                                                                    <Button
                                                                        className="m-3"
                                                                        style={{ width: "45%" }}
                                                                        onClick={(e) => {
                                                                            element.value = (element?.value || "").split(",").filter(e => element.adderBuffer2?.indexOf(e) === -1).join(",");
                                                                            setModalFormData([...(modalFormData || [])]);
                                                                        }}>
                                                                        Remove Selected
                                                                    </Button>
                                                                </div>
                                                                <Form.Select
                                                                    multiple={true}
                                                                    style={element.style}
                                                                    className={element.className}
                                                                    isInvalid={!!element.error}
                                                                    onChange={
                                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                                            const options = e.target.selectedOptions;
                                                                            // @ts-ignore
                                                                            element.adderBuffer2 = [].slice.call(options).map(o => o.value).join(",");
                                                                            setModalFormData([...(modalFormData || [])]);
                                                                        }
                                                                    }>
                                                                    {
                                                                        (element.value || "")
                                                                            .split(",").map(optionValue => <option key={optionValue} value={optionValue}>{(element?.options || []).find(o => o.value === optionValue)?.title || ""}</option>)
                                                                    }
                                                                </Form.Select>
                                                            </>
                                                        }
                                                        {
                                                            element.type === "date" &&
                                                            <Form.Control
                                                                type="date"
                                                                style={element.style}
                                                                className={element.className}
                                                                isInvalid={!!element.error}
                                                                onChange={
                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        element.value = e.target.value;
                                                                        setModalFormData([...(modalFormData || [])]);
                                                                    }
                                                                }
                                                                value={element.value}
                                                            />
                                                        }
                                                        {
                                                            element.type === "time" &&
                                                            <Form.Control
                                                                type="time"
                                                                style={element.style}
                                                                className={element.className}
                                                                isInvalid={!!element.error}
                                                                onChange={
                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        element.value = e.target.value;
                                                                        setModalFormData([...(modalFormData || [])]);
                                                                    }
                                                                }
                                                                value={element.value}
                                                            />
                                                        }
                                                        {
                                                            element.type === "datetime" &&
                                                            <Form.Control
                                                                type="datetime-local"
                                                                style={element.style}
                                                                className={element.className}
                                                                isInvalid={!!element.error}
                                                                onChange={
                                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        element.value = e.target.value;
                                                                        setModalFormData([...(modalFormData || [])]);
                                                                    }
                                                                }
                                                                value={element.value}
                                                            />
                                                        }
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                    )
                                }
                            </Form>
                        }
                    </Modal.Body>

                    {
                        (modalSettings?.cancelCallback || modalSettings?.okCallback) &&
                        <Modal.Footer>
                            {
                                modalSettings.customActionButton &&
                                <Button
                                    style={{ marginRight: "auto" }}
                                    variant={modalSettings.customActionButton.buttonType || "secondary"}
                                    onClick={() => {
                                        if (modalSettings.customActionButton?.closesModal) {
                                            setModalSettings(null);
                                            setModalFormData(null);
                                        }

                                        modalSettings.customActionButton?.onClick();
                                    }}>{modalSettings.customActionButton.title}</Button>
                            }
                            {
                                modalSettings.cancelCallback &&
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        if (modalSettings.cancelCallback) {
                                            modalSettings.cancelCallback();
                                        }
                                        setModalSettings(null);
                                        setModalFormData(null);
                                    }}>{modalSettings.cancelButtonTitle || "Cancel"}</Button>
                            }
                            {
                                modalSettings.okCallback &&
                                <Button
                                    variant="primary"
                                    onClick={modalOk}>{modalSettings.okButtonTitle || "OK"}</Button>
                            }
                        </Modal.Footer>
                    }
                </Modal>
            </GlobalContext.Provider>
        </>
    );
};

export default GlobalContextWrapper;